'use client'

import {
    fetchPlatformAccountFromProvider,
    getPlatformAccount,
    PlatformAccount,
    reconcilePlatformWebhook,
    updatePlatformCredentials,
    UpdatePlatformCredentialsData,
} from '@/lib/api/master/master-platform-account-api'
import { useEffect, useState } from 'react'
import { MdAccountBalance, MdCloudDownload, MdRefresh, MdSave, MdVisibility, MdVisibilityOff } from 'react-icons/md'
import { toast } from 'react-toastify'

const STATUS_LABEL: Record<string, string> = {
    PENDING_KYC: 'Aguardando KYC',
    ACTIVE: 'Ativa',
    BLOCKED: 'Bloqueada',
    CLOSED: 'Encerrada',
}

const STATUS_BADGE: Record<string, string> = {
    PENDING_KYC: 'badge-warning',
    ACTIVE: 'badge-success',
    BLOCKED: 'badge-error',
    CLOSED: 'badge-ghost',
}

const formatCurrency = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function PlatformAccountPage() {
    const [account, setAccount] = useState<PlatformAccount | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [showApiKey, setShowApiKey] = useState(false)
    const [fetching, setFetching] = useState(false)
    const [reconciling, setReconciling] = useState(false)

    const [form, setForm] = useState<UpdatePlatformCredentialsData>({
        apiKey: '',
        accountId: '',
        walletId: '',
        asaasUrl: '',
    })

    useEffect(() => {
        load()
    }, [])

    const load = async () => {
        setLoading(true)
        try {
            const res = await getPlatformAccount()
            if (res.success && res.result) {
                setAccount(res.result)
                setForm({
                    apiKey: '',
                    accountId: res.result.paymentAccountId ?? '',
                    walletId: res.result.paymentWalletId ?? '',
                    asaasUrl: '',
                })
            } else {
                toast.error(res.error || 'Platform account não configurada')
            }
        } finally {
            setLoading(false)
        }
    }

    const handleFetchFromProvider = async () => {
        setFetching(true)
        try {
            const res = await fetchPlatformAccountFromProvider()
            if (!res.success || !res.result) {
                toast.error(res.error || 'Não foi possível buscar do Asaas')
                return
            }

            const { accountId, walletId } = res.result
            const filled: string[] = []
            const missing: string[] = []

            setForm(prev => ({
                ...prev,
                accountId: accountId ?? prev.accountId,
                walletId: walletId ?? prev.walletId,
            }))

            if (accountId) filled.push('Account ID')
            else missing.push('Account ID')
            if (walletId) filled.push('Wallet ID')
            else missing.push('Wallet ID')

            if (filled.length > 0 && missing.length === 0) {
                toast.success(`${filled.join(' e ')} buscados — clique em Salvar para persistir`)
            } else if (filled.length > 0) {
                toast.info(
                    `${filled.join(' e ')} preenchidos. ${missing.join(' e ')} não vieram do Asaas — preencha manualmente.`,
                )
            } else {
                toast.warning('Asaas não retornou os IDs. Veja o console do backend ou preencha manualmente.')
            }
        } finally {
            setFetching(false)
        }
    }

    const handleSave = async () => {
        const payload: UpdatePlatformCredentialsData = {}
        if (form.apiKey) payload.apiKey = form.apiKey
        if (form.accountId !== account?.paymentAccountId) payload.accountId = form.accountId
        if (form.walletId !== account?.paymentWalletId) payload.walletId = form.walletId
        // asaasUrl: envia se mudou (inclusive string vazia, que apaga o override).
        if ((form.asaasUrl ?? '') !== '') payload.asaasUrl = form.asaasUrl

        if (Object.keys(payload).length === 0) {
            toast.info('Nenhuma mudança')
            return
        }

        setSaving(true)
        try {
            const res = await updatePlatformCredentials(payload)
            if (res.success) {
                toast.success('Credenciais atualizadas — override aplicado em runtime')
                const reconcile = res.result?.webhookReconcile
                if (reconcile) {
                    if (reconcile.action === 'created') toast.success('Webhook criado no Asaas')
                    else if (reconcile.action === 'updated') toast.success('Webhook atualizado no Asaas')
                    else if (reconcile.action === 'kept') toast.info('Webhook ja estava em dia')
                    else toast.warning(`Webhook nao reconciliado: ${reconcile.reason ?? 'desconhecido'}`)
                }
                setForm(prev => ({ ...prev, apiKey: '' }))
                load()
            } else {
                toast.error(res.error || 'Erro ao atualizar')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleReconcileWebhook = async () => {
        setReconciling(true)
        try {
            const res = await reconcilePlatformWebhook()
            if (!res.success || !res.result) {
                toast.error(res.error || 'Erro ao reconciliar webhook')
                return
            }
            const { action, reason } = res.result
            if (action === 'created') toast.success('Webhook criado no Asaas')
            else if (action === 'updated') toast.success('Webhook atualizado no Asaas')
            else if (action === 'kept') toast.info('Webhook ja esta em dia')
            else toast.warning(`Reconcile pulado: ${reason ?? 'verifique apiKey/accountId'}`)
        } finally {
            setReconciling(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
            </div>
        )
    }

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <MdAccountBalance /> Conta Asaas da Plataforma
                </h1>
                <p className="text-base-content/60 text-sm mt-1">
                    Credenciais master do Asaas usadas pra: criar subaccounts dos tenants, cobrar mensalidades de revendedores, payouts legacy. Override aplicado em memória — sem redeploy.
                </p>
            </div>

            {account && (
                <>
                    <div className="card bg-base-100 shadow mb-6">
                        <div className="card-body">
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <p className="text-sm text-base-content/60">Organization plataforma</p>
                                    <p className="font-medium">{account.name} (#{account.id})</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-xs btn-outline gap-1"
                                        onClick={handleReconcileWebhook}
                                        disabled={reconciling || !account.hasApiKey || !account.paymentAccountId}
                                        title="Lista/cria/atualiza o webhook no Asaas com os eventos canonicos"
                                    >
                                        {reconciling ? (
                                            <span className="loading loading-spinner loading-xs" />
                                        ) : (
                                            <MdRefresh className="text-sm" />
                                        )}
                                        Reconciliar webhook
                                    </button>
                                    <span className={`badge ${STATUS_BADGE[account.paymentAccountStatus ?? '']}`}>
                                        {STATUS_LABEL[account.paymentAccountStatus ?? ''] ?? 'Desconhecido'}
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                                <div>
                                    <p className="text-base-content/50">Provider</p>
                                    <p className="font-mono">{account.paymentProvider ?? '—'}</p>
                                </div>
                                <div>
                                    <p className="text-base-content/50">apiKey</p>
                                    <p>{account.hasApiKey ? '••••••••••' : <span className="text-error">Não configurado</span>}</p>
                                </div>
                                <div>
                                    <p className="text-base-content/50">Account ID</p>
                                    <p className="font-mono text-xs">{account.paymentAccountId ?? '—'}</p>
                                </div>
                                <div>
                                    <p className="text-base-content/50">Wallet ID</p>
                                    <p className="font-mono text-xs">{account.paymentWalletId ?? '—'}</p>
                                </div>
                                <div>
                                    <p className="text-base-content/50">Saldo cacheado</p>
                                    <p className="font-mono">{formatCurrency(account.paymentBalanceCache)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card bg-base-100 shadow">
                        <div className="card-body">
                            <h2 className="card-title">Atualizar credenciais</h2>
                            <p className="text-sm text-base-content/60 mb-4">
                                Trocar de sandbox pra produção, rotacionar token, etc. Mudança vale imediatamente em runtime. Deixe campos em branco pra não alterar.
                            </p>

                            <div className="space-y-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">apiKey (token Asaas master)</span>
                                    </label>
                                    <div className="join w-full">
                                        <input
                                            type={showApiKey ? 'text' : 'password'}
                                            className="input input-bordered join-item flex-1"
                                            placeholder="$aact_xxx... (em branco = não altera)"
                                            value={form.apiKey ?? ''}
                                            onChange={e => setForm({ ...form, apiKey: e.target.value })}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-square join-item"
                                            onClick={() => setShowApiKey(v => !v)}
                                        >
                                            {showApiKey ? <MdVisibilityOff /> : <MdVisibility />}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Account ID</span>
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-ghost gap-1"
                                            onClick={handleFetchFromProvider}
                                            disabled={fetching || !account?.hasApiKey}
                                            title={account?.hasApiKey
                                                ? 'Buscar Account ID e Wallet ID do Asaas (/myAccount)'
                                                : 'Salve a API Key antes para usar esta opção'}
                                        >
                                            {fetching ? (
                                                <span className="loading loading-spinner loading-xs"></span>
                                            ) : (
                                                <MdCloudDownload className="text-sm" />
                                            )}
                                            Buscar do Asaas
                                        </button>
                                    </label>
                                    <input
                                        className="input input-bordered font-mono text-sm"
                                        value={form.accountId ?? ''}
                                        onChange={e => setForm({ ...form, accountId: e.target.value })}
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Wallet ID</span>
                                    </label>
                                    <input
                                        className="input input-bordered font-mono text-sm"
                                        value={form.walletId ?? ''}
                                        onChange={e => setForm({ ...form, walletId: e.target.value })}
                                    />
                                </div>

                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Asaas URL override</span>
                                        <span className="label-text-alt text-base-content/50">
                                            Ex: https://sandbox.asaas.com/api/v3 — em branco apaga override
                                        </span>
                                    </label>
                                    <input
                                        className="input input-bordered font-mono text-sm"
                                        placeholder={account?.asaasUrl || '(em branco usa o env ASAAS_URL)'}
                                        value={form.asaasUrl ?? ''}
                                        onChange={e => setForm({ ...form, asaasUrl: e.target.value })}
                                    />
                                    <label className="label">
                                        <span className="label-text-alt text-base-content/60">
                                            URL em uso agora: <code className="font-mono">{account?.asaasUrl || '—'}</code>
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="card-actions justify-end mt-4">
                                <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                                    {saving ? <span className="loading loading-spinner loading-sm"></span> : <MdSave />}
                                    Salvar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
